/**
 * <license header>
 */

/**
 * Webhook operation types for Adobe Commerce webhooks.
 *
 * These operations define the different types of responses that can be returned
 * from a webhook handler to modify the behavior or data in Adobe Commerce.
 *
 * @see https://developer.adobe.com/commerce/extensibility/webhooks/
 */
export enum WebhookActionOperation {
  /** Indicates successful webhook processing with no modifications */
  SUCCESS = 'success',
  /** Indicates an exception or error occurred during webhook processing */
  EXCEPTION = 'exception',
  /** Adds a new field or value to the webhook payload */
  ADD = 'add',
  /** Replaces an existing field or value in the webhook payload */
  REPLACE = 'replace',
  /** Removes a field from the webhook payload */
  REMOVE = 'remove',
}

/**
 * Response indicating successful webhook processing.
 *
 * Use this response when the webhook has been processed successfully
 * and no modifications to the payload are needed.
 *
 * @example
 * ```typescript
 * const response: WebhookActionSuccessResponse = {
 *   op: WebhookActionOperation.SUCCESS
 * };
 * ```
 */
export interface WebhookActionSuccessResponse {
  /** Operation type */
  op: typeof WebhookActionOperation.SUCCESS;
}

/**
 * Response indicating an exception or error during webhook processing.
 *
 * Use this response to notify Adobe Commerce that an error occurred
 * during webhook processing. Optionally include the exception class
 * and error message for debugging purposes.
 *
 * @example
 * ```typescript
 * const response: WebhookActionExceptionResponse = {
 *   op: WebhookActionOperation.EXCEPTION,
 *   class: 'ProductNotFoundException',
 *   message: 'Product with SKU ABC123 not found'
 * };
 * ```
 */
export interface WebhookActionExceptionResponse {
  /** Operation type */
  op: typeof WebhookActionOperation.EXCEPTION;
  /** Optional exception class name for categorization */
  class?: string;
  /** Optional error message describing what went wrong */
  message?: string;
}

/**
 * Response for adding a new field or value to the webhook payload.
 *
 * Use this response to inject new data into the webhook payload that will
 * be processed by Adobe Commerce. The path specifies where to add the data,
 * and value contains the data to add.
 *
 * @example
 * ```typescript
 * const response: WebhookActionAddResponse = {
 *   op: WebhookActionOperation.ADD,
 *   path: 'order.items',
 *   value: { sku: 'PRODUCT-123', quantity: 2, price: 49.99 },
 *   instance: 'order-12345'
 * };
 * ```
 */
export interface WebhookActionAddResponse {
  /** Operation type */
  op: typeof WebhookActionOperation.ADD;
  /** Dot-notation path where the value should be added (e.g., 'order.items', 'customer.addresses') */
  path: string;
  /** The value to add at the specified path */
  value: any;
  /** Optional instance identifier for tracking or reference */
  instance?: string;
}

/**
 * Response for replacing an existing field or value in the webhook payload.
 *
 * Use this response to modify existing data in the webhook payload.
 * The path specifies which field to replace, and value contains the new data.
 *
 * @example
 * ```typescript
 * const response: WebhookActionReplaceResponse = {
 *   op: WebhookActionOperation.REPLACE,
 *   path: 'product.price',
 *   value: 29.99,
 *   instance: 'product-456'
 * };
 * ```
 */
export interface WebhookActionReplaceResponse {
  /** Operation type */
  op: typeof WebhookActionOperation.REPLACE;
  /** Dot-notation path to the field that should be replaced (e.g., 'product.price', 'order.status') */
  path: string;
  /** The new value to replace the existing value */
  value: any;
  /** Optional instance identifier for tracking or reference */
  instance?: string;
}

/**
 * Response for removing a field from the webhook payload.
 *
 * Use this response to remove data from the webhook payload before
 * it's processed by Adobe Commerce.
 *
 * @example
 * ```typescript
 * const response: WebhookActionRemoveResponse = {
 *   op: WebhookActionOperation.REMOVE,
 *   path: 'customer.addresses.0'
 * };
 * ```
 */
export interface WebhookActionRemoveResponse {
  /** Operation type */
  op: typeof WebhookActionOperation.REMOVE;
  /** Dot-notation path to the field that should be removed (e.g., 'items.0', 'customer.email') */
  path: string;
}

/**
 * Union type representing all possible webhook action response types.
 *
 * This type can be used when you need to handle multiple response types
 * or want to ensure type safety across different webhook operations.
 *
 * @example
 * ```typescript
 * function handleResponse(response: WebhookActionResponseType) {
 *   switch (response.op) {
 *     case WebhookActionOperation.SUCCESS:
 *       // Handle success
 *       break;
 *     case WebhookActionOperation.EXCEPTION:
 *       // Handle exception
 *       break;
 *     // ... handle other operations
 *   }
 * }
 * ```
 */
export type WebhookActionResponseType =
  | WebhookActionSuccessResponse
  | WebhookActionExceptionResponse
  | WebhookActionAddResponse
  | WebhookActionReplaceResponse
  | WebhookActionRemoveResponse;
